// Product Service - API abstraction layer (with Cache Support)
// Can fetch from CloudFront cache directly or via API Gateway
// Handles all HTTP requests to backend API
//
// ARCHITECTURE NOTE:
// - All products are cached in a single products.json file
// - Individual product lookups search the cached products array
// - This reduces S3 requests and simplifies cache invalidation

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:3001';
const CACHE_CDN_URL = process.env.REACT_APP_CACHE_CDN_URL; // Optional: CloudFront URL
const USE_DIRECT_CACHE = process.env.REACT_APP_USE_DIRECT_CACHE === 'true';

// Availability filter type
export type AvailabilityFilter = 'all' | 'in-stock' | 'out-of-stock';

// API Product type (from DynamoDB)
export interface ApiProduct {
  productId: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
  tags?: string[];
  active: boolean;
  inventory?: number;
}

// Pagination metadata
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// Paginated products response
export interface PaginatedProductsResponse {
  products: ApiProduct[];
  pagination: PaginationInfo;
}

// Query options for fetching products
export interface ProductQueryOptions {
  page?: number;
  limit?: number;
  tags?: string[];
  availability?: AvailabilityFilter;
}

// Cache response structure
interface ProductsCacheResponse {
  products: ApiProduct[];
  generatedAt: string;
  count: number;
}

interface CategoriesCacheResponse {
  categories: string[];
  generatedAt: string;
}

interface TagsCacheResponse {
  tags: string[];
  generatedAt: string;
}

// Export alias for backwards compatibility
export type { ApiProduct as Product };

// In-memory cache for direct CDN fetches
interface CacheEntry<T> {
  data: T | null;
  timestamp: number;
}

let productsCache: CacheEntry<ApiProduct[]> = { data: null, timestamp: 0 };
let categoriesCache: CacheEntry<string[]> = { data: null, timestamp: 0 };
let tagsCache: CacheEntry<string[]> = { data: null, timestamp: 0 };

const MEMORY_CACHE_TTL = 60 * 1000; // 1 minute in-memory cache

/**
 * Check if memory cache is valid
 */
function isCacheValid(cache: { timestamp: number }): boolean {
  return Date.now() - cache.timestamp < MEMORY_CACHE_TTL;
}

/**
 * Fetch from CDN cache directly with timeout
 */
async function fetchFromCDN<T>(path: string, timeoutMs: number = 5000): Promise<T | null> {
  if (!CACHE_CDN_URL) return null;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(`${CACHE_CDN_URL}/${path}`, {
      headers: {
        'Accept': 'application/json'
      },
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.warn(`CDN returned ${response.status} for ${path}`);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn('CDN fetch timed out, falling back to API');
    } else {
      console.warn('CDN fetch failed, falling back to API:', error);
    }
    return null;
  }
}

/**
 * Filter products client-side (used with direct CDN fetch)
 */
function filterProducts(
  products: ApiProduct[],
  options: {
    category?: string;
    search?: string;
    tags?: string[];
    availability?: AvailabilityFilter;
  }
): ApiProduct[] {
  let filtered = [...products];
  
  // Filter by category
  if (options.category) {
    filtered = filtered.filter(p => 
      p.category?.toLowerCase() === options.category!.toLowerCase()
    );
  }
  
  // Filter by search term
  if (options.search) {
    const searchLower = options.search.toLowerCase();
    filtered = filtered.filter(p => 
      p.name?.toLowerCase().includes(searchLower) ||
      p.description?.toLowerCase().includes(searchLower) ||
      p.category?.toLowerCase().includes(searchLower) ||
      p.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  }
  
  // Filter by tags (AND logic)
  if (options.tags && options.tags.length > 0) {
    filtered = filtered.filter(p => {
      const productTags = (p.tags || []).map(t => t.toLowerCase());
      return options.tags!.every(tag => productTags.includes(tag.toLowerCase()));
    });
  }
  
  // Filter by availability
  if (options.availability === 'in-stock') {
    filtered = filtered.filter(p => (p.inventory || 0) > 0);
  } else if (options.availability === 'out-of-stock') {
    filtered = filtered.filter(p => (p.inventory || 0) === 0);
  }
  
  return filtered;
}

/**
 * Paginate products array
 */
function paginateProducts(
  products: ApiProduct[],
  page: number,
  limit: number
): PaginatedProductsResponse {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedProducts = products.slice(startIndex, endIndex);
  const totalPages = Math.ceil(products.length / limit) || 1; // At least 1 page even if empty

  return {
    products: paginatedProducts,
    pagination: {
      page,
      limit,
      total: products.length,
      totalPages,
      hasNext: page < totalPages,
      hasPrev: page > 1
    }
  };
}

/**
 * Get all products from CDN cache
 * Returns null if cache fetch fails (to trigger API fallback)
 * Returns empty array if cache is valid but empty (no fallback needed)
 */
async function getAllProductsFromCDN(): Promise<ApiProduct[] | null> {
  // Check memory cache first
  if (isCacheValid(productsCache) && productsCache.data !== null) {
    return productsCache.data;
  }
  
  const cached = await fetchFromCDN<ProductsCacheResponse>('cache/products.json');
  
  // If CDN fetch failed, return null to trigger API fallback
  if (cached === null) {
    return null;
  }
  
  // Cache is valid (even if empty) - store it
  const products = cached.products || [];
  productsCache = { data: products, timestamp: Date.now() };
  return products;
}

/**
 * Get a single product from the CDN cache
 * Searches the products array instead of fetching individual files
 */
async function getProductFromCDN(productId: string): Promise<ApiProduct | null> {
  const products = await getAllProductsFromCDN();
  if (products === null) return null; // CDN failed, trigger API fallback
  return products.find(p => p.productId === productId) || null;
}

// Build query string from options
function buildQueryString(options: ProductQueryOptions): string {
  const params = new URLSearchParams();
  
  if (options.page) params.set('page', options.page.toString());
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.tags && options.tags.length > 0) {
    params.set('tags', options.tags.join(','));
  }
  if (options.availability && options.availability !== 'all') {
    params.set('availability', options.availability);
  }
  
  return params.toString();
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Get all products with pagination, tag filtering, and availability filtering
 */
export async function getAllProducts(
  page: number = 1, 
  limit: number = 12, 
  tags: string[] = [],
  availability: AvailabilityFilter = 'all'
): Promise<PaginatedProductsResponse> {
  // Try direct CDN fetch if enabled
  if (USE_DIRECT_CACHE && CACHE_CDN_URL) {
    try {
      const allProducts = await getAllProductsFromCDN();
      
      // Only use cache if fetch succeeded (null means CDN failed)
      if (allProducts !== null) {
        const filtered = filterProducts(allProducts, { tags, availability });
        return paginateProducts(filtered, page, limit);
      }
    } catch (error) {
      console.warn('Direct cache fetch failed, falling back to API');
    }
  }
  
  // Fallback to API
  try {
    const queryString = buildQueryString({ page, limit, tags, availability });
    const response = await fetch(`${API_ENDPOINT}/api/products?${queryString}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }

    const data = await response.json();
    return {
      products: data.products || [],
      pagination: data.pagination
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

/**
 * Get products by category with pagination, tag filtering, and availability filtering
 */
export async function getProductsByCategory(
  category: string, 
  page: number = 1, 
  limit: number = 12, 
  tags: string[] = [],
  availability: AvailabilityFilter = 'all'
): Promise<PaginatedProductsResponse> {
  // Try direct CDN fetch if enabled
  if (USE_DIRECT_CACHE && CACHE_CDN_URL) {
    try {
      const allProducts = await getAllProductsFromCDN();
      
      // Only use cache if fetch succeeded (null means CDN failed)
      if (allProducts !== null) {
        const filtered = filterProducts(allProducts, { category, tags, availability });
        return paginateProducts(filtered, page, limit);
      }
    } catch (error) {
      console.warn('Direct cache fetch failed, falling back to API');
    }
  }
  
  // Fallback to API
  try {
    const queryString = buildQueryString({ page, limit, tags, availability });
    const response = await fetch(
      `${API_ENDPOINT}/api/products?category=${encodeURIComponent(category)}&${queryString}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }

    const data = await response.json();
    return {
      products: data.products || [],
      pagination: data.pagination
    };
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
}

/**
 * Get single product
 */
export async function getProduct(productId: string): Promise<ApiProduct> {
  // Validate product ID format
  if (!productId || !/^[a-zA-Z0-9_-]{1,50}$/.test(productId)) {
    throw new Error('Invalid product ID');
  }
  
  // Try direct CDN fetch if enabled - now searches products.json
  if (USE_DIRECT_CACHE && CACHE_CDN_URL) {
    try {
      const cached = await getProductFromCDN(productId);
      if (cached) return cached;
      // If cached is null, it could mean:
      // 1. CDN failed (getAllProductsFromCDN returned null) - fall through to API
      // 2. Product not in cache - fall through to API (might be newly added)
    } catch (error) {
      console.warn('Direct cache fetch failed, falling back to API');
    }
  }
  
  // Fallback to API
  try {
    const response = await fetch(`${API_ENDPOINT}/api/products/${encodeURIComponent(productId)}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product not found');
      }
      throw new Error('Failed to fetch product');
    }

    const data = await response.json();
    return data.product;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
}

/**
 * Search products with pagination, tag filtering, and availability filtering
 */
export async function searchProducts(
  searchTerm: string, 
  page: number = 1, 
  limit: number = 12, 
  tags: string[] = [],
  availability: AvailabilityFilter = 'all'
): Promise<PaginatedProductsResponse> {
  // Validate search term
  if (searchTerm && searchTerm.length > 100) {
    throw new Error('Search term too long');
  }
  
  // Try direct CDN fetch if enabled
  if (USE_DIRECT_CACHE && CACHE_CDN_URL) {
    try {
      const allProducts = await getAllProductsFromCDN();
      
      // Only use cache if fetch succeeded (null means CDN failed)
      if (allProducts !== null) {
        const filtered = filterProducts(allProducts, { search: searchTerm, tags, availability });
        return paginateProducts(filtered, page, limit);
      }
    } catch (error) {
      console.warn('Direct cache fetch failed, falling back to API');
    }
  }
  
  // Fallback to API
  try {
    const queryString = buildQueryString({ page, limit, tags, availability });
    const response = await fetch(
      `${API_ENDPOINT}/api/products?search=${encodeURIComponent(searchTerm)}&${queryString}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to search products');
    }

    const data = await response.json();
    return {
      products: data.products || [],
      pagination: data.pagination
    };
  } catch (error) {
    console.error('Error searching products:', error);
    throw error;
  }
}

/**
 * Get all categories
 */
export async function getCategories(): Promise<string[]> {
  // Try direct CDN fetch if enabled
  if (USE_DIRECT_CACHE && CACHE_CDN_URL) {
    // Check memory cache
    if (isCacheValid(categoriesCache) && categoriesCache.data) {
      return categoriesCache.data;
    }
    
    try {
      const cached = await fetchFromCDN<CategoriesCacheResponse>('cache/categories.json');
      if (cached?.categories) {
        categoriesCache = { data: cached.categories, timestamp: Date.now() };
        return cached.categories;
      }
    } catch (error) {
      console.warn('Direct cache fetch failed, falling back to API');
    }
  }
  
  // Fallback to API
  try {
    const response = await fetch(`${API_ENDPOINT}/api/categories`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }

    const data = await response.json();
    return data.categories || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
}

/**
 * Get all tags
 */
export async function getTags(): Promise<string[]> {
  // Try direct CDN fetch if enabled
  if (USE_DIRECT_CACHE && CACHE_CDN_URL) {
    // Check memory cache
    if (isCacheValid(tagsCache) && tagsCache.data) {
      return tagsCache.data;
    }
    
    try {
      const cached = await fetchFromCDN<TagsCacheResponse>('cache/tags.json');
      if (cached?.tags) {
        tagsCache = { data: cached.tags, timestamp: Date.now() };
        return cached.tags;
      }
    } catch (error) {
      console.warn('Direct cache fetch failed, falling back to API');
    }
  }
  
  // Fallback to API
  try {
    const response = await fetch(`${API_ENDPOINT}/api/tags`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch tags');
    }

    const data = await response.json();
    return data.tags || [];
  } catch (error) {
    console.error('Error fetching tags:', error);
    throw error;
  }
}

/**
 * Clear local memory cache (useful after admin makes changes)
 * Call this after admin creates/updates/deletes products to force fresh data
 */
export function clearLocalCache(): void {
  productsCache = { data: null, timestamp: 0 };
  categoriesCache = { data: null, timestamp: 0 };
  tagsCache = { data: null, timestamp: 0 };
}

/**
 * Get cache status (for debugging)
 */
export function getCacheStatus(): { 
  productsAge: number | null;
  categoriesAge: number | null;
  tagsAge: number | null;
  cdnUrl: string | undefined;
  directCacheEnabled: boolean;
} {
  const now = Date.now();
  return {
    productsAge: productsCache.timestamp ? now - productsCache.timestamp : null,
    categoriesAge: categoriesCache.timestamp ? now - categoriesCache.timestamp : null,
    tagsAge: tagsCache.timestamp ? now - tagsCache.timestamp : null,
    cdnUrl: CACHE_CDN_URL,
    directCacheEnabled: USE_DIRECT_CACHE
  };
}